import LoaderIcon from '@icons/Loader.svg';
/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { STORAGE_KEYS } from '../../../constants';
import { useTokenValidation } from '../../../hooks/auth';
import { useGlobalStore } from '../../../stores/global/global.store';
import { Button } from '../../../ui-library/Elements/Button';
import { generateProofKey, getAuthRedirect, setAuthToken } from '../../../utils/auth';
import { getLocalStorageItem, removeLocalStorageItem } from '../../../utils/storage';
import { getWindowStatus } from '../../../utils/window';

enum LoginActions {
  JOIN_NOW = 'join-now',
  SIGN_IN = 'sign-in',
}

export const UserLogin = () => {
  const router = useRouter();
  const { t } = useTranslation('common', { keyPrefix: 'common' });
  const { setNotification } = useGlobalStore();
  const { data: userToken, error, status: tokenValidationStatus } = useTokenValidation();

  const [isLoading, setIsLoading] = useState(false);

  const windowStatus = getWindowStatus();
  const codeVerifier = windowStatus ? getLocalStorageItem(STORAGE_KEYS.AUTH_CODE_VERIFIER) : undefined;
  const authRedirect = getAuthRedirect();

  useEffect(() => {
    if (codeVerifier) {
      if (userToken?.access_token) {
        setAuthToken(userToken.access_token, userToken.expires_in);
        void router.push(authRedirect);
      } else {
        setIsLoading(true);
        if (tokenValidationStatus === 'error') {
          if (error.message !== 'incomplete') {
            const path = window.location.pathname;
            void router.replace(path, undefined, { shallow: true });
            removeLocalStorageItem(STORAGE_KEYS.AUTH_CODE_VERIFIER);
            setNotification({
              display: true,
              title: t('notifications.error.login.title'),
              description: t('notifications.error.login.description'),
              theme: 'error',
            });
          }
          setIsLoading(false);
        }
      }
    }
  }, [tokenValidationStatus, userToken, error]);

  const userAuth = async (action: LoginActions) => {
    try {
      const proofKey = await generateProofKey();
      const codeChallenge = proofKey?.challenge;
      if (codeChallenge) {
        const params = new URLSearchParams({
          client_id: process.env.NEXT_PUBLIC_BA_CLIENT_ID,
          response_type: 'code',
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
          redirect_uri: process.env.NEXT_PUBLIC_BA_REDIRECT,
          action: action,
        });
        void open(`${process.env.NEXT_PUBLIC_BA_URL}/sso/authorize?${params.toString()}`, '_self');
      } else {
        setNotification({
          display: true,
          title: t('notifications.error.generic.title'),
          description: t('notifications.error.generic.description'),
          theme: 'error',
        });
      }
    } catch {
      setNotification({
        display: true,
        title: t('notifications.error.generic.title'),
        description: t('notifications.error.generic.description'),
        theme: 'error',
      });
    }
  };

  return (
    <div className='mt-4 lg:mt-10 inline-grid w-full auto-cols-auto grid-flow-row gap-4 sm:w-auto sm:auto-cols-[1fr] sm:grid-flow-col'>
      <Button
        onClick={() => {
          void userAuth(LoginActions.JOIN_NOW);
        }}
        variant='primary'
        className='w-full sm:w-auto sm:min-w-40'
        disabled={isLoading}
        data-cy='sign-up-btn'
        data-loading={isLoading}
      >
        {t(LoginActions.JOIN_NOW)}
      </Button>
      <Button
        onClick={() => {
          void userAuth(LoginActions.SIGN_IN);
        }}
        variant='secondary'
        className='w-full sm:w-auto sm:min-w-40'
        disabled={isLoading}
        icon={isLoading ? LoaderIcon : undefined}
        iconPosition='before'
        iconClassName='animate-spin-grow'
        data-cy='login-btn'
        data-loading={isLoading}
      >
        {t(LoginActions.SIGN_IN)}
      </Button>
    </div>
  );
};
